import { AuthService } from './endpoints/AuthService';
import { saveTokensToCookies } from './config';
import { getLocalAuthStorage } from 'utils/localStorage';
import { localStorageKeys } from 'constants/localStorage';
import { isPublicRoute } from 'constants/routes';

/* ============================================================================
 * MARINERS-2011 — "Manter conectado": estado da re-autenticação.
 * ----------------------------------------------------------------------------
 * Ponte entre `refreshAuthLogic` (código não-React, dentro do interceptor) e a
 * ReauthModal (React). Store minúsculo com subscribe, e não Context: quem precisa
 * ABRIR a modal é um interceptor de axios, que não tem árvore de componentes por
 * onde chamar um setState.
 *
 * ── Por que isto substitui um `window.location.replace('/logout')` ───────────
 * O refresh já existia e funcionava (axios-auth-refresh + Mutex). O que ele fazia
 * ao falhar era navegar para /logout — página inteira, contexto todo perdido.
 * Aqui a navegação vira uma PROMISE PENDENTE: `refreshAuthLogic` fica parado
 * esperando a senha, e é isso que preserva o trabalho do usuário.
 *
 * ── A fila do D1 já existe; não foi preciso construí-la ──────────────────────
 * `mutex.runExclusive` serializa todas as chamadas a `refreshAuthLogic`, e o
 * `axios-auth-refresh` segura as requests novas enquanto o refresh está em voo
 * ("while refreshAuthCall is running, all the new requests are intercepted and
 * are waiting for the refresh call to resolve" — contrato da lib, v3.2.1).
 * Segurar a promise enquanto a modal está aberta faz a fila existente esperar,
 * em vez de exigir uma fila nova. Verificado: com o líder pendente, as requests
 * represadas sobrevivem e destravam na ordem quando ele resolve — e não há
 * `timeout` de axios em lugar nenhum do projeto, então "indefinida" (D5) é
 * literal.
 *
 * ⚠️ ANTI-LOOP: o POST de re-login vai com `skipAuthRefresh: true`. Sem isso o
 * `checkIfSessionExpired` do `AxiosMainLogin` chamaria `refreshAuthLogic` de
 * dentro do mutex JÁ TOMADO por esta re-autenticação — mutex não reentrante,
 * deadlock. A flag faz o interceptor retornar antes de tocar no refresh
 * (checkIfSessionExpired:6). É a mesma válvula que `AuthService.refreshToken`
 * já usava.
 * ========================================================================== */

/** Tentativas de senha antes do logout completo (D4). */
export const MAX_TENTATIVAS = 3;

export type ReauthEstado = {
  /** A modal deve estar na tela. */
  ativo: boolean;
  /** Login do usuário da sessão morta — a modal mostra, não deixa editar. */
  login: string | null;
  /** Nome de exibição, quando o `lkp_auth_ctx` tinha. */
  nomeExibicao: string | null;
  /** Quantas tentativas ainda restam. */
  tentativasRestantes: number;
  /** Mensagem de erro da última tentativa, para o erro inline. */
  erro: string | null;
  /** Submit em andamento — trava o botão. */
  enviando: boolean;
  /** O backend recusou o re-login porque a conta tem sessão ativa em outro lugar
   *  (401 + `hasActiveSession`). A senha estava CERTA — ver `submeterSenha`. */
  sessaoAtivaEmOutroLugar: boolean;
};

const ESTADO_INICIAL: ReauthEstado = {
  ativo: false,
  login: null,
  nomeExibicao: null,
  tentativasRestantes: MAX_TENTATIVAS,
  erro: null,
  enviando: false,
  sessaoAtivaEmOutroLugar: false,
};

let estado: ReauthEstado = ESTADO_INICIAL;
const ouvintes = new Set<() => void>();

/** Promise que o `refreshAuthLogic` está esperando. Enquanto existir, há uma
 *  re-autenticação em curso e o mutex do refresh está tomado. */
let pendente: { resolve: () => void; reject: (motivo: Error) => void } | null = null;

const emitir = () => ouvintes.forEach(ouvinte => ouvinte());

const setEstado = (patch: Partial<ReauthEstado>) => {
  estado = { ...estado, ...patch };
  emitir();
};

export const subscribe = (ouvinte: () => void): (() => void) => {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
};

/** Snapshot estável — `useSyncExternalStore` compara por identidade e entraria em
 *  laço infinito se isto montasse um objeto novo a cada chamada. */
export const getEstado = (): ReauthEstado => estado;

/** Só para teste: devolve o store ao zero entre casos. */
export const _resetParaTeste = () => {
  estado = ESTADO_INICIAL;
  pendente = null;
  ouvintes.clear();
};

/** true enquanto a re-autenticação está em curso — consumido pelo gate de modais. */
export const reauthEmCurso = (): boolean => estado.ativo;

export type SessaoNova = { token: string; refreshToken: string; expiresIn: string };
export type SincronizadorDeSessao = (sessao: SessaoNova) => void;

/** Ponte para o `AuthContext` do React. O store não pode chamar `setState`; quem pode é
 *  o Host, que registra o setter aqui ao montar. */
let sincronizarContexto: SincronizadorDeSessao | null = null;

/**
 * Registra quem sincroniza o AuthContext do React após o re-login.
 *
 * Por que registrar em vez de o Host observar o store e reagir: ORDEM. O contexto precisa
 * estar atualizado ANTES de a fila destravar (ver `aplicarSessao`). Um efeito reagindo à
 * mudança de estado rodaria depois do resolve — tarde demais.
 *
 * Devolve o descadastro, para o Host usar direto no cleanup do `useEffect`.
 */
export const registrarSincronizadorDeSessao = (fn: SincronizadorDeSessao): (() => void) => {
  sincronizarContexto = fn;
  return () => {
    if (sincronizarContexto === fn) sincronizarContexto = null;
  };
};

/**
 * Chamado pelo `refreshAuthLogic` quando a sessão caiu de vez.
 *
 * Devolve uma promise que resolve quando o usuário se re-autentica (e aí o refresh
 * segue com o token novo) ou rejeita quando ele desiste (3 falhas ou "outra conta"),
 * caso em que o chamador faz o logout completo.
 *
 * Em ROTA PÚBLICA não abre modal: rejeita na hora e o chamador mantém o
 * comportamento de hoje. Sem sessão, /qr e /chamado seguem funcionando.
 */
export const solicitarReautenticacao = (): Promise<void> => {
  if (isPublicRoute(window.location.pathname)) {
    // `name` e não texto de mensagem: é por este símbolo que `pedirReautenticacao`
    // decide NÃO fazer replace — em rota pública a falha é só uma request rejeitada.
    const erro = new Error('reauth: rota pública, sem modal e sem navegação');
    erro.name = 'ReauthRotaPublica';
    return Promise.reject(erro);
  }

  // Só pode haver UMA re-autenticação por vez. Com o mutex do refresh isso já é
  // verdade hoje, mas a garantia mora aqui e não numa suposição sobre o chamador.
  if (pendente) return new Promise<void>(() => {});

  const storage = getLocalAuthStorage();
  setEstado({
    ativo: true,
    login: storage?.user?.name ?? null,
    nomeExibicao: storage?.user?.fullName ?? storage?.user?.name ?? null,
    tentativasRestantes: MAX_TENTATIVAS,
    erro: null,
    enviando: false,
    sessaoAtivaEmOutroLugar: false,
  });

  return new Promise<void>((resolve, reject) => {
    pendente = { resolve, reject };
  });
};

/**
 * Grava a sessão nova nos DOIS lados.
 *
 * `saveTokensToCookies` é o helper que já existia — reusado, não reimplementado:
 * é ele que grava com `domain: URLS.cookieDomain`, e é esse domínio compartilhado
 * que mantém o legado VB.NET autenticado (achado do V4: não existe chamada de login
 * legada a replicar; o legado lê o cookie).
 *
 * ⚠️ `lkp_permission_ctx` NÃO é tocado — decisão revogada depois do reconhecimento:
 * o login não devolve permissões (elas vêm do menu, por empresa/unidade), então não
 * há "resposta nova" de onde tirá-las. As permissões seguem o ciclo natural de
 * atualização do app, igual a uma sessão que já dura horas. Limitação conhecida e
 * deliberada, registrada no PR.
 *
 * O contexto de NAVEGAÇÃO (cookie `companies`, `lkp_hf_ctx`) também não é tocado —
 * é justamente o que não pode se perder.
 */
const aplicarSessao = (data: any) => {
  const { token, refreshToken, expiresIn } = data.authToken;

  /* ⚠️ ORDEM IMPORTA, e as três escritas têm que terminar ANTES de a fila destravar.
     Se a fila saísse primeiro, a primeira request represada passaria pelo
     `addBearerTokenInReq`, releria o COOKIE velho e levaria outro 401 — a mesma
     armadilha do lado inverso. Por isso `aplicarSessao` é síncrona e o `resolve()`
     do chamador vem depois dela. */

  // 1. localStorage. Antes dos cookies de propósito: é daqui que o `checkIfSessionExpired`
  //    lê o `expiresIn`. Se ficasse velho (no passado), a PRÓXIMA request dispararia o
  //    refresh de novo e reabriria a modal com credencial válida — loop silencioso.
  const storage = getLocalAuthStorage() || {};
  localStorage.setItem(
    localStorageKeys.authContext,
    JSON.stringify({ ...storage, isAuthenticated: true, statusCode: true, token, refreshToken, expiresIn }),
  );

  // 2. Cookies, pelo helper existente — é ele que grava com `domain: URLS.cookieDomain`,
  //    e é esse domínio compartilhado que mantém o legado VB.NET autenticado (V4).
  saveTokensToCookies(token, refreshToken, expiresIn);

  // 3. AuthContext do React, para o app não precisar de reload.
  //    Atualização MÍNIMA (spread do anterior + só os campos de sessão): mapeados os 43
  //    consumidores, nenhum reage destrutivamente — não há `key` derivado de token em
  //    lugar nenhum, o `PrivateRoute` lê o localStorage e não o contexto, e os únicos três
  //    efeitos com dep de token são tag do Sentry, um `useCallback` e um redirect que só
  //    existe em `/login` (rota pública, onde esta modal nem abre). Ainda assim se
  //    preserva o resto do objeto: trocar o objeto inteiro descartaria `user` e qualquer
  //    campo que o contexto tenha e o storage não.
  sincronizarContexto?.({ token, refreshToken, expiresIn });
};

/** Mensagem de falha de credencial. Genérica de propósito: o backend não distingue
 *  senha errada de sessão revogada (não há código nem payload no 401 além do
 *  `hasActiveSession`, que é exclusivo de "já logado em outro lugar"). */
const ERRO_CREDENCIAL = 'Senha incorreta.';
const ERRO_GENERICO = 'Não foi possível entrar. Tente novamente.';

export interface SubmeterOptions {
  /** Reenvia derrubando a sessão que estiver ativa em outro dispositivo. Só é usado
   *  depois de o backend responder `hasActiveSession` — nunca no primeiro envio. */
  encerrarOutraSessao?: boolean;
}

/**
 * Tenta a re-autenticação com a senha digitada.
 *
 * Devolve `true` no sucesso. No fracasso consome uma tentativa; esgotadas as 3,
 * abandona (rejeita a fila) e o chamador faz o logout completo.
 *
 * ⚠️ EXCEÇÃO — `hasActiveSession` NÃO consome tentativa. Ver o bloco abaixo.
 */
export const submeterSenha = async (
  senha: string,
  { encerrarOutraSessao = false }: SubmeterOptions = {},
): Promise<boolean> => {
  if (!pendente || estado.enviando) return false;
  setEstado({ enviando: true, erro: null });

  try {
    const resposta: any = await AuthService.login(
      {
        login: estado.login as string,
        password: senha,
        // Só vai `true` quando o usuário JÁ viu o aviso e escolheu derrubar a outra
        // sessão. Mandar direto no primeiro envio derrubaria o outro dispositivo do
        // usuário sem perguntar — e a tela de login, para o mesmo evento, pergunta.
        expireCurrentSession: encerrarOutraSessao,
        // ⚠️ `stayConnected` da sessão original NÃO é recuperável: o campo existe em
        // `lkp_auth_ctx` (AuthContext/initialState) mas é um `true` fixo que o
        // `onLogin` nunca atualiza a partir do checkbox — ler dali seria ler uma
        // constante, não a escolha do usuário. Default `false`, documentado no PR.
        // Consequência conhecida e limitada: se o backend deixar de emitir
        // refreshToken, a próxima expiração reabre esta modal em vez de renovar
        // sozinha — que é o comportamento novo, não uma regressão.
        stayConnected: false,
      },
      // Allowlist anti-loop: sem isto o POST entraria no `checkIfSessionExpired` e
      // chamaria `refreshAuthLogic` de dentro do mutex já tomado → deadlock.
      { skipAuthRefresh: true },
    );

    /* ⚠️ CONTA ATIVA EM OUTRO LUGAR — o caminho que quase passou batido.
     *
     * Este 401 chega RESOLVIDO, não rejeitado: `handleAuthError` trata
     * `401 + hasActiveSession` como caso especial e faz `Promise.resolve(error.response)`
     * (handleAuthError:13). Sem tratar aqui, ele caía no `throw` abaixo, virava erro
     * genérico ("Não foi possível entrar") e QUEIMAVA UMA TENTATIVA — com a senha certa.
     * Três dessas e o usuário era deslogado sem nunca saber a causa real.
     *
     * E não é caso de canto: se a sessão caiu PORQUE alguém entrou na conta em outro
     * dispositivo, este é o caminho mais provável de todos.
     *
     * Não consome tentativa (a credencial estava correta) e não fecha a modal: ela troca
     * para o estado de acesso exclusivo, onde o usuário decide derrubar a outra sessão. */
    if (resposta?.data?.hasActiveSession) {
      setEstado({ enviando: false, sessaoAtivaEmOutroLugar: true, erro: null });
      return false;
    }

    if (!resposta?.data?.authToken?.token) throw new Error('resposta de login sem token');

    aplicarSessao(resposta.data);
    const resolver = pendente.resolve;
    pendente = null;
    setEstado({ ...ESTADO_INICIAL });
    resolver();
    return true;
  } catch (erro: any) {
    // `handleAuthError` rejeita com a RESPONSE (não com um Error) no POST /v1/auth —
    // por isso o status é procurado nos dois formatos.
    const status = erro?.status ?? erro?.response?.status;

    // Fix pós-review: só 401 (credencial recusada) consome tentativa. Falha de rede,
    // 5xx ou resposta malformada não dizem NADA sobre a senha — decrementar aqui
    // deslogava o usuário por causa de um backend instável, com a senha certa no campo.
    // O usuário pode reenviar à vontade; a saída continua existindo ("outra conta").
    if (status !== 401) {
      setEstado({ enviando: false, erro: ERRO_GENERICO });
      return false;
    }

    const restantes = estado.tentativasRestantes - 1;

    if (restantes <= 0) {
      abandonar('tentativas esgotadas');
      return false;
    }

    setEstado({
      enviando: false,
      tentativasRestantes: restantes,
      erro: ERRO_CREDENCIAL,
    });
    return false;
  }
};

/**
 * Desiste da re-autenticação: rejeita a fila para o chamador fazer o logout
 * completo. Usado pelas 3 falhas de senha e pelo "Entrar com outra conta".
 */
export const abandonar = (motivo: string): void => {
  const rejeitar = pendente?.reject;
  pendente = null;
  setEstado({ ...ESTADO_INICIAL });
  rejeitar?.(new Error(`reauth abandonada: ${motivo}`));
};
